import type {
  AcceptResponse,
  ApiRequest,
  CandidateProfileAnalyticsRequest,
  PreflightFailure,
  PreflightResponse,
  PreflightSuccess,
  PublishRequest,
} from 'src/types/api';

import { isHttpError, postJson } from './http';

export const ACCEPT_PATH = '/api/application-prefill/accept';
export const PREFLIGHT_PATH = '/api/application-prefill/preflight';
export const PUBLISH_PATH = '/api/application-prefill/publish';
export const STORE_ANALYTICS_PATH =
  '/api/application-prefill/storeProfileAnalytics';

export interface ApiClient {
  preflight: (request: ApiRequest) => Promise<PreflightResponse>;
  accept: (request: ApiRequest) => Promise<AcceptResponse>;
  publish: (request: PublishRequest) => Promise<void>;
  storeProfileAnalytics: (
    request: CandidateProfileAnalyticsRequest,
  ) => Promise<void>;
}

export const onApiRequest = async (
  onSuccess: () => Promise<void>,
  onFailure: (err: unknown) => void,
) => {
  try {
    await onSuccess();
  } catch (err) {
    onFailure(err);
  }
};

export const apiClient: ApiClient = {
  preflight: async (request: ApiRequest): Promise<PreflightResponse> => {
    try {
      const result = await postJson<PreflightSuccess>({
        attempts: 3,
        request,
        // If preflight isn't working, there's a good chance accept isn't either.
        timeout: 5000,
        url: PREFLIGHT_PATH,
      });

      return {
        ...result,
        ok: true,
      };
    } catch (err: unknown) {
      if (isHttpError(err, 400)) {
        return { ...(err.json as PreflightFailure), ok: false };
      }

      throw err;
    }
  },

  accept: async (request: ApiRequest): Promise<AcceptResponse> => {
    const { code } = await postJson<AcceptResponse>({
      attempts: 3,
      request,
      // We can't successfully redirect back to the apply form without a code.
      timeout: 5000,
      url: ACCEPT_PATH,
    });

    return { code };
  },

  publish: async (request: PublishRequest): Promise<void> => {
    try {
      await postJson({
        attempts: 1,
        request,
        timeout: 2500,
        url: PUBLISH_PATH,
        // Ensure the request outlives the app when closed
        keepalive: true,
      });
    } catch {
      // Ignore any errors when trying to publish metrics. The user doesn't
      // need this request to succeed.
    }
  },

  storeProfileAnalytics: async (
    request: CandidateProfileAnalyticsRequest,
  ): Promise<void> =>
    postJson({
      attempts: 1,
      request,
      timeout: 2000,
      url: STORE_ANALYTICS_PATH,
    }),
};
